import React, { useState, useMemo } from 'react';
import ProgressSteps from '../components/ToolBox/ProgressSteps';
import Step1_Configure from '../components/ToolBox/FlexibleExtract/Step1_Configure';
import Step2_Scraping from '../components/ToolBox/FlexibleExtract/Step2_Scraping';
import Step3_Analyze from '../components/ToolBox/FlexibleExtract/Step3_Analyze';
import Step4_Preview from '../components/ToolBox/FlexibleExtract/Step3_Preview';
import Step5_Complete from '../components/ToolBox/FlexibleExtract/Step4_Complete';

function normalizeToRows(results) {
  const rows = [];
  for (const r of results) {
    if (!r.success || !r.data) continue;
    const d = r.data;
    if (Array.isArray(d)) {
      d.forEach((item) => rows.push({ _url: r.url, ...item }));
    } else if (d.items && Array.isArray(d.items)) {
      d.items.forEach((item) => rows.push({ _url: r.url, ...item }));
    } else if (typeof d === 'object') {
      rows.push({ _url: r.url, ...d });
    }
  }
  return rows;
}

const DEFAULT_LANGUAGES = [
  { code: 'en_US', label: 'en_US' },
  { code: 'zh_HK', label: 'zh_HK' },
  { code: 'zh_CN', label: 'zh_CN' },
];

const FlexibleExtract = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [urls, setUrls] = useState([]);
  const [urlPool, setUrlPool] = useState([]);
  const [urlRows, setUrlRows] = useState([]);
  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [fields, setFields] = useState([]);
  const [scrapeResults, setScrapeResults] = useState([]);
  const [analyzeResults, setAnalyzeResults] = useState([]);
  const [error, setError] = useState('');

  const rowCount = useMemo(() => normalizeToRows(analyzeResults).length, [analyzeResults]);
  const urlCount = analyzeResults.filter((r) => r.success).length;

  const isMultiMode = selectedLanguages.length >= 2;
  const selectedLangsList = languages.filter((l) => selectedLanguages.includes(l.code));

  const getAllUrls = () => {
    if (!isMultiMode) return urls;
    const set = new Set();
    urlRows.forEach((row) => {
      selectedLangsList.forEach((l) => {
        const u = row[l.code];
        if (u) set.add(u);
      });
    });
    return Array.from(set);
  };

  const handleStep1Next = () => {
    setError('');
    const allUrls = getAllUrls();
    if (allUrls.length === 0) {
      setError(isMultiMode ? 'Add URLs to the table (drag from pool or paste in cells)' : 'Add at least one URL');
      return;
    }
    if (!(instruction || '').trim()) {
      setError('Enter an instruction (prompt)');
      return;
    }
    const validFields = fields.filter((f) => (f.name || '').trim() && (f.description || '').trim());
    if (validFields.length === 0) {
      setError('Add at least one field with name and description');
      return;
    }
    setFields(validFields);
    setCurrentStep(1);
  };

  const handleStep2Complete = (results) => {
    setScrapeResults(results);
    setCurrentStep(2);
  };

  const handleStep3Complete = (results) => {
    setAnalyzeResults(results);
    setCurrentStep(3);
  };

  const handleStep4Complete = () => {
    setCurrentStep(4);
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setUrls([]);
    setUrlPool([]);
    setUrlRows([]);
    setLanguages(DEFAULT_LANGUAGES);
    setSelectedLanguages([]);
    setInstruction('');
    setFields([]);
    setScrapeResults([]);
    setAnalyzeResults([]);
    setError('');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Flexible URL Extractor</h1>
          <p className="text-gray-600 mt-1">Extract structured data from many pages (e.g. service + service_doctor_list) and export CSV.</p>
        </div>

        <ProgressSteps currentStep={currentStep + 1} totalSteps={5} />

        {currentStep === 0 && (
          <Step1_Configure
            urlPool={urlPool}
            setUrlPool={setUrlPool}
            urlRows={urlRows}
            setUrlRows={setUrlRows}
            urls={urls}
            setUrls={setUrls}
            languages={languages}
            setLanguages={setLanguages}
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={setSelectedLanguages}
            instruction={instruction}
            setInstruction={setInstruction}
            fields={fields}
            setFields={setFields}
            onNext={handleStep1Next}
            error={error}
          />
        )}

        {currentStep === 1 && (
          <Step2_Scraping
            urls={getAllUrls()}
            onComplete={handleStep2Complete}
            onBack={() => setCurrentStep(0)}
          />
        )}

        {currentStep === 2 && (
          <Step3_Analyze
            scrapeResults={scrapeResults}
            urlRows={urlRows}
            urls={urls}
            languages={languages}
            selectedLanguages={selectedLanguages}
            isMultiMode={isMultiMode}
            instruction={instruction}
            fields={fields}
            onComplete={handleStep3Complete}
            onBack={() => setCurrentStep(1)}
          />
        )}

        {currentStep === 3 && (
          <Step4_Preview
            results={analyzeResults}
            onBack={() => setCurrentStep(2)}
            onComplete={handleStep4Complete}
          />
        )}

        {currentStep === 4 && (
          <Step5_Complete
            rowCount={rowCount}
            urlCount={urlCount}
            onRestart={handleRestart}
          />
        )}
      </div>
    </div>
  );
};

export default FlexibleExtract;
